/**
 * Apple Store Purchase Data Formatting Utility
 *
 * RevenueCat handles all StoreKit billing interactions (purchase, restore, entitlements).
 * This module formats Apple purchase records for database storage and processes
 * App Store Server Notifications V2 for subscription lifecycle events.
 */

import { Platform } from 'react-native';

import { APPLE_PRODUCTS } from '../config/platformProducts';

type ApplePurchasePayload = {
  transactionId?: string;
  originalTransactionId?: string;
  productId: string;
  purchaseTime?: number;
  expirationDate?: number;
  isInIntroOfferPeriod?: boolean;
  isTrialPeriod?: boolean;
  cancellationDate?: number;
};

export interface AppleStorePurchase {
  transactionId: string;
  originalTransactionId: string;
  productId: string;
  purchaseDate: number;
  expirationDate?: number;
  isInIntroOfferPeriod?: boolean;
  isTrialPeriod?: boolean;
  cancellationDate?: number;
  environment: 'sandbox' | 'production';
}

export interface AppleStoreSubscriptionStatus {
  originalTransactionId: string;
  status: 'active' | 'expired' | 'billing_retry' | 'billing_grace_period' | 'revoked';
  expirationDate: number;
  autoRenewStatus: boolean;
  autoRenewProductId?: string;
  gracePeriodExpirationDate?: number;
  isInBillingRetryPeriod?: boolean;
  expirationIntent?: number;
}

class AppleStoreBillingHandler {
  /**
   * Check if running on iOS platform
   */
  isIOS(): boolean {
    return Platform.OS === 'ios';
  }

  /**
   * Get Apple App Store product IDs
   */
  getProductIds(): string[] {
    return APPLE_PRODUCTS.map(product => product.id);
  }

  /**
   * Process Apple Store purchase
   * Extracts iOS-specific purchase data
   */
  processAppleStorePurchase(purchase: ApplePurchasePayload): AppleStorePurchase | null {
    if (!this.isIOS()) {
      console.warn('processAppleStorePurchase called on non-iOS platform');
      return null;
    }

    try {
      return {
        transactionId: purchase.transactionId || '',
        originalTransactionId: purchase.originalTransactionId || purchase.transactionId || '',
        productId: purchase.productId,
        purchaseDate: purchase.purchaseTime || Date.now(),
        expirationDate: purchase.expirationDate,
        isInIntroOfferPeriod: purchase.isInIntroOfferPeriod,
        isTrialPeriod: purchase.isTrialPeriod,
        cancellationDate: purchase.cancellationDate,
        environment: __DEV__ ? 'sandbox' : 'production'
      };
    } catch (error) {
      console.error('Error processing Apple Store purchase:', error);
      return null;
    }
  }

  /**
   * Prepare purchase data for backend verification
   * Returns data needed for App Store Server API verification
   */
  preparePurchaseForVerification(purchase: AppleStorePurchase): {
    transactionId: string;
    originalTransactionId: string;
    productId: string;
    environment: 'sandbox' | 'production';
  } {
    return {
      transactionId: purchase.transactionId,
      originalTransactionId: purchase.originalTransactionId,
      productId: purchase.productId,
      environment: purchase.environment
    };
  }

  /**
   * Handle Apple Store notification types (Server Notifications V2)
   * Maps notification types to subscription states
   *
   * Notification Types:
   * - SUBSCRIBED: Initial purchase
   * - DID_RENEW: Successful renewal
   * - DID_FAIL_TO_RENEW: Renewal failed
   * - DID_CHANGE_RENEWAL_STATUS: Auto-renew toggled
   * - DID_CHANGE_RENEWAL_PREF: User changed subscription tier
   * - EXPIRED: Subscription expired
   * - GRACE_PERIOD_EXPIRED: Grace period ended
   * - OFFER_REDEEMED: Promotional offer redeemed
   * - PRICE_INCREASE: Price increase accepted
   * - REFUND: Subscription refunded
   * - REFUND_DECLINED: Refund request declined
   * - RENEWAL_EXTENDED: Renewal date extended
   * - REVOKE: Purchase revoked
   * - TEST: Test notification
   */
  parseNotificationType(notificationType: string): {
    type: string;
    shouldGrantAccess: boolean;
    shouldRevokeAccess: boolean;
  } {
    const typeMap: Record<string, { type: string; shouldGrantAccess: boolean; shouldRevokeAccess: boolean }> = {
      'SUBSCRIBED': { type: 'SUBSCRIBED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'DID_RENEW': { type: 'RENEWED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'DID_FAIL_TO_RENEW': { type: 'RENEWAL_FAILED', shouldGrantAccess: false, shouldRevokeAccess: false },
      'DID_CHANGE_RENEWAL_STATUS': { type: 'RENEWAL_STATUS_CHANGED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'DID_CHANGE_RENEWAL_PREF': { type: 'TIER_CHANGED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'EXPIRED': { type: 'EXPIRED', shouldGrantAccess: false, shouldRevokeAccess: true },
      'GRACE_PERIOD_EXPIRED': { type: 'GRACE_PERIOD_EXPIRED', shouldGrantAccess: false, shouldRevokeAccess: true },
      'OFFER_REDEEMED': { type: 'OFFER_REDEEMED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'PRICE_INCREASE': { type: 'PRICE_INCREASE_ACCEPTED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'REFUND': { type: 'REFUNDED', shouldGrantAccess: false, shouldRevokeAccess: true },
      'REFUND_DECLINED': { type: 'REFUND_DECLINED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'RENEWAL_EXTENDED': { type: 'RENEWAL_EXTENDED', shouldGrantAccess: true, shouldRevokeAccess: false },
      'REVOKE': { type: 'REVOKED', shouldGrantAccess: false, shouldRevokeAccess: true },
      'TEST': { type: 'TEST', shouldGrantAccess: false, shouldRevokeAccess: false }
    };

    return typeMap[notificationType] || {
      type: 'UNKNOWN',
      shouldGrantAccess: false,
      shouldRevokeAccess: false
    };
  }

  /**
   * Parse Apple Store expiration intent
   * Reason why subscription expired
   *
   * 1 = Customer canceled
   * 2 = Billing error
   * 3 = Customer did not agree to price increase
   * 4 = Product not available at renewal time
   * 5 = Unknown error
   */
  parseExpirationIntent(expirationIntent?: number): string {
    const intentMap: Record<number, string> = {
      1: 'CUSTOMER_CANCELED',
      2: 'BILLING_ERROR',
      3: 'PRICE_INCREASE_DECLINED',
      4: 'PRODUCT_UNAVAILABLE',
      5: 'UNKNOWN_ERROR'
    };

    return intentMap[expirationIntent || 5] || 'UNKNOWN';
  }

  /**
   * Determine if subscription is active based on status
   */
  isSubscriptionActive(status: AppleStoreSubscriptionStatus): boolean {
    const now = Date.now();

    // Check if subscription is active
    if (status.status !== 'active' && status.status !== 'billing_grace_period') {
      return false;
    }

    // Check if subscription hasn't expired
    if (status.expirationDate && now >= status.expirationDate) {
      return false;
    }

    return true;
  }

  /**
   * Get subscription grace period status
   * Grace period allows continued access while billing issues resolve
   */
  isInGracePeriod(status: AppleStoreSubscriptionStatus): boolean {
    return (
      status.status === 'billing_grace_period' &&
      status.gracePeriodExpirationDate !== undefined &&
      Date.now() < status.gracePeriodExpirationDate
    );
  }

  /**
   * Get subscription billing retry status
   * Billing retry means Apple is attempting to collect payment
   */
  isInBillingRetry(status: AppleStoreSubscriptionStatus): boolean {
    return status.isInBillingRetryPeriod || false;
  }

  /**
   * Check if subscription is in trial period
   */
  isInTrialPeriod(purchase: AppleStorePurchase): boolean {
    return purchase.isTrialPeriod || false;
  }

  /**
   * Check if subscription is in intro offer period
   */
  isInIntroOfferPeriod(purchase: AppleStorePurchase): boolean {
    return purchase.isInIntroOfferPeriod || false;
  }

  /**
   * Check if purchase was refunded
   */
  wasRefunded(purchase: AppleStorePurchase): boolean {
    return purchase.cancellationDate !== undefined;
  }

  /**
   * Format purchase data for database storage
   */
  formatForDatabase(purchase: AppleStorePurchase, userId: string) {
    const planMapping = this.getProductPlanMapping(purchase.productId);

    return {
      user_id: userId,
      plan_id: planMapping?.planId || '',
      billing_interval: planMapping?.billingInterval || 'monthly',
      payment_provider: 'apple' as const,
      payment_provider_id: purchase.transactionId,
      platform: 'ios' as const,
      original_transaction_id: purchase.originalTransactionId,
      environment: purchase.environment,
      status: this.determineSubscriptionStatus(purchase),
      is_trial_period: purchase.isTrialPeriod,
      is_intro_offer_period: purchase.isInIntroOfferPeriod
    };
  }

  /**
   * Determine subscription status from purchase data
   */
  private determineSubscriptionStatus(purchase: AppleStorePurchase): string {
    if (purchase.cancellationDate) {
      return 'canceled';
    }

    if (purchase.expirationDate && Date.now() >= purchase.expirationDate) {
      return 'expired';
    }

    return 'active';
  }

  /**
   * Get plan mapping for Apple product ID
   */
  private getProductPlanMapping(productId: string): { planId: string; billingInterval: 'monthly' | 'annual' } | null {
    const product = APPLE_PRODUCTS.find(p => p.id === productId);
    if (!product) {
      return null;
    }

    return {
      planId: product.planId,
      billingInterval: product.billingInterval
    };
  }

  /**
   * Get Apple App Store subscription management URL
   */
  getSubscriptionManagementUrl(): string {
    return 'https://apps.apple.com/account/subscriptions';
  }

  /**
   * Validate transaction ID format
   */
  isValidTransactionId(transactionId: string): boolean {
    // Apple transaction IDs are typically numeric strings
    // Basic validation: not empty, reasonable length, numeric
    return (
      transactionId.length > 5 &&
      transactionId.length < 50 &&
      /^[0-9]+$/.test(transactionId)
    );
  }

  /**
   * Get product display name from product ID
   */
  getProductDisplayName(productId: string): string {
    const product = APPLE_PRODUCTS.find(p => p.id === productId);
    return product?.displayName || productId;
  }

  /**
   * Get all Apple products for a specific plan
   */
  getProductsByPlanId(planId: string): typeof APPLE_PRODUCTS {
    if (planId === 'premium') {
      return APPLE_PRODUCTS;
    }

    return APPLE_PRODUCTS.filter(p => p.planId === planId);
  }

  /**
   * Parse product ID to extract components
   * Format: com.kingdombiblequiz.kingdombiblequiz.{plan}.{interval}
   */
  parseProductId(productId: string): { bundleId: string; planId: 'premium'; interval: string } | null {
    const product = APPLE_PRODUCTS.find((currentProduct) => currentProduct.id === productId);
    if (!product) {
      return null;
    }

    const interval = product.billingInterval === 'annual' ? 'annual' : 'monthly';

    return {
      bundleId: productId.split('.').slice(0, 3).join('.'),
      planId: 'premium',
      interval,
    };
  }

  /**
   * Get App Store Connect product configuration URL
   */
  getProductConfigUrl(): string {
    return 'https://appstoreconnect.apple.com/apps/{app_id}/subscriptions';
  }

  /**
   * Check if subscription supports family sharing
   * (Based on product configuration in App Store Connect)
   */
  supportsFamilySharing(productId: string): boolean {
    const product = APPLE_PRODUCTS.find(p => p.id === productId);
    return product?.planId === 'premium';
  }

  /**
   * Get subscription group identifier
   * All products in same group can be upgraded/downgraded
   */
  getSubscriptionGroupId(productId: string): string {
    const product = APPLE_PRODUCTS.find(p => p.id === productId);
    return product?.planId === 'premium' ? 'premium_subscriptions' : 'premium_subscriptions';
  }
}

// Export singleton instance
export const appleStoreBillingHandler = new AppleStoreBillingHandler();
